<script setup lang="ts">
import { computed, useTemplateRef } from 'vue';
import { animations } from '@/services/Animations';

type PhotoIndex = 0 | 1;
type WebcamCaptureState =
  | 'PHOTO_NOT_TAKEN'
  | 'PHOTO_TAKEN_1'
  | 'PHOTO_RETAKING'
  | 'PHOTO_TAKEN_2';

interface WebcamCaptureStates {
  PHOTO_NOT_TAKEN: 'PHOTO_NOT_TAKEN';
  PHOTO_TAKEN_1: 'PHOTO_TAKEN_1';
  PHOTO_RETAKING: 'PHOTO_RETAKING';
  PHOTO_TAKEN_2: 'PHOTO_TAKEN_2';
}

interface Props {
  currentState: WebcamCaptureState;
  states: WebcamCaptureStates;
  isWebcamReady: boolean;
  webcamInitFailed: boolean;
  isPhotographingInProgress: boolean;
  isUploadingInProgress: boolean;
  countdown: number | null;
  isShowingTakenPhoto: boolean;
  showUploadFromPhoneButton: boolean;
  uploadFromPhoneLink: string;
}

interface UsePhotoPayload {
  photoIndex: PhotoIndex;
  animateFromRef: HTMLElement;
}

interface Emits {
  retryCamera: [];
  takePhoto: [photoIndex: PhotoIndex];
  retryPhoto: [];
  usePhoto: [payload: UsePhotoPayload];
}

const props = defineProps<Props>();
const emit = defineEmits<Emits>();

// biome-ignore lint/correctness/noUnusedVariables: used in <template>
const titleKey = computed((): string => {
  if (props.webcamInitFailed) {
    return 'PhotoFromWebcam.titleCameraError';
  }
  if (props.isPhotographingInProgress) {
    return 'PhotoFromWebcam.titleAttention';
  }
  if (props.isUploadingInProgress) {
    return 'PhotoFromWebcam.titleUpload';
  }
  if (props.isShowingTakenPhoto) {
    return 'PhotoFromWebcam.titleReview';
  }
  return 'PhotoFromWebcam.titleGetReady';
});

const countdownElement = useTemplateRef<HTMLSpanElement>('countdown');
const initialTakeButton =
  useTemplateRef<HTMLButtonElement>('initialTakeButton');
const firstPhotoUseButton = useTemplateRef<HTMLButtonElement>(
  'firstPhotoUseButton'
);
const retakeButton = useTemplateRef<HTMLButtonElement>('retakeButton');
const secondPhotoUseButton = useTemplateRef<HTMLButtonElement>(
  'secondPhotoUseButton'
);
const uploadFromPhoneIcon = useTemplateRef<HTMLElement>('uploadFromPhoneIcon');

// biome-ignore lint/correctness/noUnusedVariables: used in <template>
function onUsePhotoClick(photoIndex: PhotoIndex, event: MouseEvent): void {
  emit('usePhoto', {
    photoIndex,
    animateFromRef: event.currentTarget as HTMLElement,
  });
}

function pulseInitialActions(): void {
  if (initialTakeButton.value) {
    animations.pulseBox(initialTakeButton.value, { count: 3 });
  }
}

function pulseUsePhoto(photoIndex: PhotoIndex): void {
  const button =
    photoIndex === 0 ? firstPhotoUseButton.value : secondPhotoUseButton.value;
  if (button) {
    animations.pulseBox(button, { count: 3 });
  }
}

function pulseRetakeTake(): void {
  if (retakeButton.value) {
    animations.pulseBox(retakeButton.value, { count: 3 });
  }
}

function pulseCountdown(duration: number): void {
  if (countdownElement.value) {
    animations.pulseText(countdownElement.value, { duration });
  }
}

function buzzUploadFromPhone(): void {
  if (uploadFromPhoneIcon.value) {
    animations.buzz(uploadFromPhoneIcon.value, { gap: 2000, count: 5 });
  }
}

defineExpose({
  pulseInitialActions,
  pulseUsePhoto,
  pulseRetakeTake,
  pulseCountdown,
  buzzUploadFromPhone,
});
</script>

<template>
  <div class="PhotoFromWebcam__content" data-testid="webcam-capture-controls">
    <h1
      class="PhotoFromWebcam__title kiosk-title"
      data-testid="webcam-capture-title"
    >
      {{ $t(titleKey) || (props.webcamInitFailed ? 'Camera unavailable' : '') }}
    </h1>

    <span
      ref="countdown"
      class="PhotoFromWebcam__countdown"
      data-testid="webcam-capture-countdown"
      v-show="props.isPhotographingInProgress && props.countdown"
    >
      {{ props.countdown }}
    </span>

    <div
      v-if="props.webcamInitFailed"
      class="PhotoFromWebcam__error-actions"
      data-testid="photo-webcam-error-actions"
    >
      <button
        class="kiosk-button"
        data-testid="webcam-retry-camera"
        @click="emit('retryCamera')"
      >
        {{ $t('PhotoFromWebcam.buttonRetryCamera') || 'Retry Camera' }}
      </button>
    </div>

    <div
      class="PhotoFromWebcam__actions"
      :class="{
        'PhotoFromWebcam__actions--active':
          props.isWebcamReady &&
          !props.isPhotographingInProgress &&
          !props.isUploadingInProgress,
      }"
      :data-testid="
        props.isWebcamReady &&
        !props.isPhotographingInProgress &&
        !props.isUploadingInProgress
          ? 'webcam-ready'
          : undefined
      "
    >
      <div
        v-show="props.currentState === props.states.PHOTO_NOT_TAKEN"
        class="PhotoFromWebcam__actions-inner"
        data-testid="webcam-initial-actions"
      >
        <RouterLink
          v-show="props.showUploadFromPhoneButton"
          class="kiosk-button-inline"
          data-testid="webcam-skip-to-mobile"
          :to="props.uploadFromPhoneLink"
        >
          <i
            ref="uploadFromPhoneIcon"
            class="PhotoFromWebcam__phone-icon"
          />
          {{ $t('PhotoFromWebcam.buttonSecondaryUpload') }}
        </RouterLink>

        <button
          ref="initialTakeButton"
          class="kiosk-button"
          data-testid="button-take-photo"
          @click="emit('takePhoto', 0)"
        >
          {{ $t('PhotoFromWebcam.buttonPrimaryPhoto') }}
        </button>
      </div>

      <div
        v-show="props.currentState === props.states.PHOTO_TAKEN_1"
        class="PhotoFromWebcam__actions-inner"
        data-testid="webcam-review-first-actions"
      >
        <button
          class="kiosk-button-inline"
          data-testid="button-retake-photo"
          @click="emit('retryPhoto')"
        >
          {{ $t('PhotoFromWebcam.buttonSecondaryRetake') }}
        </button>

        <button
          ref="firstPhotoUseButton"
          class="kiosk-button"
          data-testid="button-use-photo"
          @click="onUsePhotoClick(0, $event)"
        >
          {{ $t('PhotoFromWebcam.buttonPrimaryUse') }}
        </button>
      </div>

      <div
        v-show="props.currentState === props.states.PHOTO_RETAKING"
        class="PhotoFromWebcam__actions-inner"
        data-testid="webcam-retaking-actions"
      >
        <button
          ref="retakeButton"
          class="kiosk-button"
          data-testid="button-take-photo-retake"
          @click="emit('takePhoto', 1)"
        >
          {{ $t('PhotoFromWebcam.buttonPrimaryRetake') }}
        </button>
      </div>

      <div
        v-show="props.currentState === props.states.PHOTO_TAKEN_2"
        class="PhotoFromWebcam__actions-inner"
        data-testid="webcam-review-second-actions"
      >
        <button
          class="kiosk-button-inline"
          data-testid="button-use-first-photo"
          @click="onUsePhotoClick(0, $event)"
        >
          {{ $t('PhotoFromWebcam.buttonSecondaryUseFirst') }}
        </button>

        <button
          ref="secondPhotoUseButton"
          class="kiosk-button"
          data-testid="button-use-photo-1"
          @click="onUsePhotoClick(1, $event)"
        >
          {{ $t('PhotoFromWebcam.buttonPrimaryUse') }}
        </button>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
.PhotoFromWebcam__content {
  position: relative;
  padding:
    var(--Kiosk--wrapper-top-gap) var(--Kiosk--wrapper-side-gap) 10rem
    var(--Kiosk--wrapper-side-gap);
  display: flex;
  align-items: center;
  flex-direction: column;
  z-index: 3;
}

.PhotoFromWebcam__title {
  text-shadow: 0 0.2rem 0.2rem rgba(#000000, 0.2);
  color: #ffffff;
  padding: var(--photo-from-webcam-title-padding, 0);
  margin-top: var(--photo-from-webcam-title-margin-top, 0);
}

.PhotoFromWebcam__countdown {
  color: #ffffff;
  font-size: 5rem;
  margin-top: var(--photo-from-webcam-countdown-margin-top, 1rem);
  text-shadow: 0 0.2rem 0.2rem rgba(#000000, 0.2);
}

.PhotoFromWebcam__error-actions {
  margin-top: 2rem;
  display: flex;
  justify-content: center;
  z-index: 4;

  .kiosk-button {
    min-width: 18rem;
  }
}

.PhotoFromWebcam__actions {
  margin-top: auto;
  margin-bottom: var(--photo-from-webcam-actions-margin-bottom, 0);
  opacity: 0;
  // Invisible must also mean untappable — opacity alone still hit-tests, so
  // "Take a Photo" was reachable at 311x62 while fully transparent. The
  // --active modifier already flips this back to `all`.
  pointer-events: none;
  width: 100%;
  display: flex;
  justify-content: center;

  &--active {
    pointer-events: all;
    transition: opacity 200ms;
    opacity: 1;
  }
}

.PhotoFromWebcam__actions-inner {
  display: flex;
  flex-direction: var(--photo-from-webcam-actions-direction, row);
  justify-content: center;
  align-items: center;

  .kiosk-button {
    min-width: var(--photo-from-webcam-button-min-width, 18rem);
    height: var(--photo-from-webcam-button-height, 3.6rem);
    font-size: var(--photo-from-webcam-button-font-size, 1.6rem);

    // When it's the only child (upload button hidden), make it wider
    &:only-child {
      min-width: 24rem;
    }
  }

  .kiosk-button-inline {
    text-shadow: 0 0.2rem 0.2rem rgba(#000000, 0.2);
    color: #ffffff;
    text-decoration-color: rgba(#ffffff, 0.3);
    height: var(--photo-from-webcam-inline-button-height, 3.6rem);
    font-size: var(--photo-from-webcam-inline-button-font-size, 1.55rem);
  }

  & > * {
    margin-top: var(--photo-from-webcam-action-margin-top, 0);
    margin-right: var(--photo-from-webcam-action-margin-right, 3.5rem);

    &:last-child {
      margin-right: 0;
    }
  }
}

.PhotoFromWebcam__phone-icon {
  display: inline-block;
  width: 1.3rem;
  height: 1.3rem;
  transform: rotate(35deg);
  margin-right: 0.4em;
  margin-top: -0.1rem;
  background: #ffffff;

  mask: {
    image: url('../../../assets/images/mobile-phone.svg');
    size: contain;
    repeat: no-repeat;
    position: center;
  };
}

</style>
